import React from 'react';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import AllInclusiveRoundedIcon from '@mui/icons-material/AllInclusiveRounded';
import {
  PACKAGE_STATUS, expirationInfo, formatBRL, formatDate, isOpenPackage, sessionsLabel,
} from './walletFormat';
import styles from './PackageCard.module.css';

/** Uma linha por procedimento: nome, barra fina e "4 de 10 sessões". */
function PackageItem({ item, muted }) {
  const remaining = Number(item.credits_remaining) || 0;
  const hasTotal = item.credits_total !== null && item.credits_total !== undefined && Number(item.credits_total) > 0;
  const total = hasTotal ? Number(item.credits_total) : null;
  const ratio = hasTotal ? Math.max(0, Math.min(1, remaining / total)) : null;
  const fillTone = muted ? styles.fillMuted : (remaining === 1 ? styles.fillWarning : '');

  return (
    <li className={styles.item}>
      <div className={styles.itemRow}>
        <span className={styles.itemName} title={item.procedure_name}>{item.procedure_name}</span>
        <span className={styles.itemCount}>
          {hasTotal
            ? <><strong>{remaining}</strong> de {total} {total === 1 ? 'sessão' : 'sessões'}</>
            : <><strong>{remaining}</strong> {remaining === 1 ? 'sessão restante' : 'sessões restantes'}</>}
        </span>
      </div>
      {hasTotal && (
        <div
          className={styles.track}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={remaining}
          aria-label={`${item.procedure_name}: ${sessionsLabel(remaining)} de ${total}`}
        >
          <span className={`${styles.fill} ${fillTone}`} style={{ width: `${ratio * 100}%` }} />
        </div>
      )}
    </li>
  );
}

/**
 * Cartão de um pacote comprado: nome (ou "Créditos anteriores"), compra, valor,
 * validade, pílula de estado e as sessões por procedimento.
 */
function PackageCard({ pkg }) {
  const open = isOpenPackage(pkg);
  const legacy = !pkg.package_name;
  const status = PACKAGE_STATUS[pkg.status] || PACKAGE_STATUS.active;
  const expiration = expirationInfo(pkg.expiration_date, pkg.status);
  const purchased = formatDate(pkg.purchased_at);
  const price = pkg.price === null || pkg.price === undefined ? '' : formatBRL(pkg.price);
  // Cada pedaço fica inteiro numa linha: no cartão estreito a quebra acontece
  // no separador, nunca no meio do valor ("R$" numa linha, "1.000,00" na outra)
  const metaParts = [purchased ? `Comprado em ${purchased}` : '', price].filter(Boolean);

  return (
    <article className={`${styles.card} ${open ? '' : styles.closed}`}>
      <header className={styles.head}>
        <span className={styles.icon}>
          {legacy ? <HistoryOutlinedIcon fontSize="small" /> : <Inventory2OutlinedIcon fontSize="small" />}
        </span>
        <div className={styles.identity}>
          <span className={styles.name}>{legacy ? 'Créditos anteriores' : pkg.package_name}</span>
          {metaParts.length > 0
            ? (
              <span className={styles.meta}>
                {metaParts.map((part, index) => (
                  <React.Fragment key={part}>
                    {index > 0 && ' · '}
                    <span className={styles.metaPart}>{part}</span>
                  </React.Fragment>
                ))}
              </span>
            )
            : <span className={styles.meta}>Sessões compradas antes do controle por pacote</span>}
        </div>
        <div className={styles.side}>
          <span className={`${styles.status} ${styles[status.tone]}`}>{status.label}</span>
          <span className={`${styles.validity} ${styles[`validity_${expiration.tone}`]}`}>
            {expiration.noExpiry
              ? <AllInclusiveRoundedIcon className={styles.validityIcon} />
              : <EventAvailableOutlinedIcon className={styles.validityIcon} />}
            {expiration.label}
          </span>
        </div>
      </header>

      <ul className={styles.items}>
        {pkg.items.map((item) => (
          <PackageItem key={item.credit_id || item.procedure_id} item={item} muted={!open} />
        ))}
      </ul>
    </article>
  );
}

export default PackageCard;
